import React, { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { 
  Upload, 
  Palette, 
  Globe, 
  Code, 
  Eye,
  DollarSign,
  Users,
  Building2,
  TrendingUp,
  BarChart3
} from "lucide-react";

// Configuration Form Component
export const ConfigurationForm = ({ 
  form, 
  setForm, 
  onSubmit, 
  onCancel, 
  onFileUpload, 
  isEditing 
}) => {
  const [uploading, setUploading] = useState({});
  const [previewColors, setPreviewColors] = useState(false);

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (field, file) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [field]: true }));
    const assetType = field === 'logo_url' ? 'logo' : 
                     field === 'favicon_url' ? 'favicon' : 'background';
    
    const uploadedUrl = await onFileUpload(file, assetType);
    if (uploadedUrl) {
      handleInputChange(field, uploadedUrl);
    }
    
    setUploading(prev => ({ ...prev, [field]: false }));
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="branding" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="colors">Colors</TabsTrigger>
          <TabsTrigger value="domain">Domain</TabsTrigger>
          <TabsTrigger value="advanced">Advanced</TabsTrigger>
        </TabsList>

        {/* Branding Tab */}
        <TabsContent value="branding" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company_name">Company Name *</Label>
              <Input
                id="company_name"
                value={form.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Your Company Name"
              />
            </div>
            
            <div>
              <Label htmlFor="tenant_id">Tenant ID *</Label>
              <Input
                id="tenant_id"
                value={form.tenant_id}
                onChange={(e) => handleInputChange('tenant_id', e.target.value)}
                placeholder="unique-tenant-id"
                disabled={isEditing}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="logo_upload">Logo Upload</Label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml"
                  onChange={(e) => handleFileChange('logo_url', e.target.files[0])}
                  disabled={uploading.logo_url}
                />
                {form.logo_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={form.logo_url} 
                      alt="Logo preview" 
                      className="h-8 w-auto"
                    />
                    <span className="text-sm text-green-600">✓ Uploaded</span>
                  </div>
                )}
                <Input
                  placeholder="Or enter logo URL"
                  value={form.logo_url}
                  onChange={(e) => handleInputChange('logo_url', e.target.value)}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="favicon_upload">Favicon Upload</Label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/x-icon,image/png"
                  onChange={(e) => handleFileChange('favicon_url', e.target.files[0])}
                  disabled={uploading.favicon_url}
                />
                {form.favicon_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={form.favicon_url} 
                      alt="Favicon preview" 
                      className="h-4 w-4"
                    />
                    <span className="text-sm text-green-600">✓ Uploaded</span>
                  </div>
                )}
                <Input
                  placeholder="Or enter favicon URL"
                  value={form.favicon_url}
                  onChange={(e) => handleInputChange('favicon_url', e.target.value)}
                />
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="font_family">Font Family</Label>
            <Select value={form.font_family} onValueChange={(value) => handleInputChange('font_family', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Inter">Inter (Default)</SelectItem>
                <SelectItem value="Roboto">Roboto</SelectItem>
                <SelectItem value="Open Sans">Open Sans</SelectItem>
                <SelectItem value="Lato">Lato</SelectItem>
                <SelectItem value="Poppins">Poppins</SelectItem>
                <SelectItem value="Montserrat">Montserrat</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </TabsContent>

        {/* Colors Tab */}
        <TabsContent value="colors" className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">Color Scheme</h4>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewColors(!previewColors)}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="primary_color">Primary Color</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={form.primary_color}
                  onChange={(e) => handleInputChange('primary_color', e.target.value)}
                  className="w-12 h-10 p-1"
                />
                <Input
                  value={form.primary_color}
                  onChange={(e) => handleInputChange('primary_color', e.target.value)}
                  placeholder="#1f2937"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="secondary_color">Secondary Color</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={form.secondary_color}
                  onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                  className="w-12 h-10 p-1"
                />
                <Input
                  value={form.secondary_color}
                  onChange={(e) => handleInputChange('secondary_color', e.target.value)}
                  placeholder="#3b82f6"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="accent_color">Accent Color</Label>
              <div className="flex space-x-2">
                <Input
                  type="color"
                  value={form.accent_color}
                  onChange={(e) => handleInputChange('accent_color', e.target.value)}
                  className="w-12 h-10 p-1"
                />
                <Input
                  value={form.accent_color}
                  onChange={(e) => handleInputChange('accent_color', e.target.value)}
                  placeholder="#10b981"
                />
              </div>
            </div>
          </div>

          {previewColors && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle style={{ color: form.primary_color }}>
                  Preview: {form.company_name || 'Your Company'}
                </CardTitle>
                <CardDescription>This is how your brand colors will look</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Button style={{ backgroundColor: form.secondary_color }}>
                    Primary Button
                  </Button>
                  <Button 
                    variant="outline"
                    style={{ 
                      borderColor: form.accent_color, 
                      color: form.accent_color 
                    }}
                  >
                    Secondary Button
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Domain Tab */}
        <TabsContent value="domain" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="subdomain">Subdomain</Label>
              <div className="flex">
                <Input
                  value={form.subdomain}
                  onChange={(e) => handleInputChange('subdomain', e.target.value)}
                  placeholder="client-name"
                />
                <span className="inline-flex items-center px-3 border border-l-0 bg-gray-50 text-gray-500 text-sm">
                  .yourdomain.com
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                This will be accessible at {form.subdomain || 'subdomain'}.yourdomain.com
              </p>
            </div>

            <div>
              <Label htmlFor="custom_domain">Custom Domain</Label>
              <Input
                value={form.custom_domain}
                onChange={(e) => handleInputChange('custom_domain', e.target.value)}
                placeholder="client.example.com"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Use client's own domain
              </p>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Domain Setup Instructions</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Subdomain: Automatically configured and ready to use</li>
              <li>• Custom Domain: Client needs to add CNAME record pointing to your server</li>
              <li>• SSL certificates are automatically managed</li>
            </ul>
          </div>
        </TabsContent>

        {/* Advanced Tab */}
        <TabsContent value="advanced" className="space-y-4">
          <div>
            <Label htmlFor="custom_css">Custom CSS</Label>
            <Textarea
              value={form.custom_css}
              onChange={(e) => handleInputChange('custom_css', e.target.value)}
              placeholder="/* Custom CSS styles */&#10;.custom-header {&#10;  background: #your-color;&#10;}"
              rows={8}
            />
            <p className="text-xs text-gray-500 mt-1">
              Advanced: Add custom CSS to override default styles
            </p>
          </div>

          <div>
            <Label htmlFor="login_background">Custom Login Background</Label>
            <div className="space-y-2">
              <Input
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => handleFileChange('custom_login_background', e.target.files[0])}
                disabled={uploading.custom_login_background}
              />
              <Input
                placeholder="Or enter background image URL"
                value={form.custom_login_background}
                onChange={(e) => handleInputChange('custom_login_background', e.target.value)}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="hide_footer_branding"
              checked={form.hide_footer_branding}
              onChange={(e) => handleInputChange('hide_footer_branding', e.target.checked)}
            />
            <Label htmlFor="hide_footer_branding">
              Hide "Powered by TavPlans" footer branding
            </Label>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          onClick={onSubmit}
          disabled={!form.company_name || !form.tenant_id}
        >
          {isEditing ? 'Update Configuration' : 'Create Configuration'}
        </Button>
      </div>
    </div>
  );
};

// Partner Form Component
export const PartnerForm = ({ form, setForm, onSubmit, onCancel }) => {
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleFeatureToggle = (feature) => {
    setForm(prev => ({
      ...prev,
      allowed_features: prev.allowed_features.includes(feature)
        ? prev.allowed_features.filter(f => f !== feature)
        : [...prev.allowed_features, feature]
    }));
  };

  const availableFeatures = [
    'basic_planning',
    'advanced_planning', 
    'scenario_modeling',
    'cost_center_management',
    'business_intelligence',
    'white_labeling',
    'api_access'
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="partner_name">Partner Name *</Label>
          <Input
            id="partner_name"
            value={form.partner_name}
            onChange={(e) => handleInputChange('partner_name', e.target.value)}
            placeholder="Acme Corp"
          />
        </div>

        <div>
          <Label htmlFor="contact_email">Contact Email *</Label>
          <Input
            type="email"
            id="contact_email"
            value={form.contact_email}
            onChange={(e) => handleInputChange('contact_email', e.target.value)}
            placeholder="partner@example.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contact_phone">Contact Phone</Label>
          <Input
            id="contact_phone"
            value={form.contact_phone}
            onChange={(e) => handleInputChange('contact_phone', e.target.value)}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div>
          <Label htmlFor="max_clients">Max Clients</Label>
          <Input
            type="number"
            id="max_clients"
            value={form.max_clients}
            onChange={(e) => handleInputChange('max_clients', parseInt(e.target.value) || -1)}
            placeholder="-1 for unlimited"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="company_address">Company Address</Label>
        <Textarea
          id="company_address"
          value={form.company_address}
          onChange={(e) => handleInputChange('company_address', e.target.value)}
          placeholder="123 Main St, City, State, Country"
          rows={2}
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label htmlFor="revenue_share_percentage">Revenue Share (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            step="0.1"
            id="revenue_share_percentage"
            value={form.revenue_share_percentage}
            onChange={(e) => handleInputChange('revenue_share_percentage', parseFloat(e.target.value) || 0)}
          />
        </div>

        <div>
          <Label htmlFor="commission_rate">Commission Rate (%)</Label>
          <Input
            type="number"
            min="0"
            max="100"
            step="0.1"
            id="commission_rate"
            value={form.commission_rate}
            onChange={(e) => handleInputChange('commission_rate', parseFloat(e.target.value) || 0)}
          />
        </div>

        <div>
          <Label htmlFor="default_tier">Default Tier</Label>
          <Select value={form.default_tier} onValueChange={(value) => handleInputChange('default_tier', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Allowed Features</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {availableFeatures.map((feature) => (
            <div key={feature} className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={feature}
                checked={form.allowed_features.includes(feature)}
                onChange={() => handleFeatureToggle(feature)}
              />
              <Label htmlFor={feature} className="text-sm capitalize">
                {feature.replace('_', ' ')}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          onClick={onSubmit}
          disabled={!form.partner_name || !form.contact_email}
        >
          Create Partner
        </Button>
      </div>
    </div>
  );
};

// Tenant Form Component
export const TenantForm = ({ form, setForm, partners, onSubmit, onCancel }) => {
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="tenant_name">Tenant Name *</Label>
        <Input
          id="tenant_name"
          value={form.tenant_name}
          onChange={(e) => handleInputChange('tenant_name', e.target.value)}
          placeholder="Client Company Name"
        />
      </div>

      <div>
        <Label htmlFor="contact_email">Contact Email *</Label>
        <Input
          type="email"
          id="contact_email"
          value={form.contact_email}
          onChange={(e) => handleInputChange('contact_email', e.target.value)}
          placeholder="client@example.com"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="partner_id">Partner (Optional)</Label>
          <Select value={form.partner_id} onValueChange={(value) => handleInputChange('partner_id', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select partner" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">No Partner</SelectItem>
              {partners.map((partner) => (
                <SelectItem key={partner.id} value={partner.id}>
                  {partner.partner_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="subscription_tier">Subscription Tier</Label>
          <Select value={form.subscription_tier} onValueChange={(value) => handleInputChange('subscription_tier', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starter">Starter</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          onClick={onSubmit}
          disabled={!form.tenant_name || !form.contact_email}
        >
          Create Tenant
        </Button>
      </div>
    </div>
  );
};

// Analytics Dashboard Component
export const AnalyticsDashboard = ({ configs, partners, tenants }) => {
  const totalRevenue = tenants.reduce((sum, tenant) => sum + tenant.monthly_revenue, 0);
  const totalUsers = tenants.reduce((sum, tenant) => sum + tenant.total_users, 0);
  const activeConfigs = configs.filter(c => c.is_active).length;

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">White-Label Analytics</h2>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Total Revenue</CardDescription>
              <DollarSign className="h-4 w-4 text-green-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${totalRevenue.toLocaleString()}
            </div>
            <p className="text-xs text-gray-600">Monthly recurring</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Active Configs</CardDescription>
              <Palette className="h-4 w-4 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {activeConfigs}
            </div>
            <p className="text-xs text-gray-600">of {configs.length} total</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Partners</CardDescription>
              <Building2 className="h-4 w-4 text-purple-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {partners.length}
            </div>
            <p className="text-xs text-gray-600">Active resellers</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Total Users</CardDescription>
              <Users className="h-4 w-4 text-orange-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {totalUsers}
            </div>
            <p className="text-xs text-gray-600">Across all tenants</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Revenue by Partner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {partners.map((partner) => {
                const partnerTenants = tenants.filter(t => t.partner_id === partner.id);
                const partnerRevenue = partnerTenants.reduce((sum, t) => sum + t.monthly_revenue, 0);
                
                return (
                  <div key={partner.id} className="flex justify-between items-center">
                    <span className="text-sm">{partner.partner_name}</span>
                    <span className="font-medium">${partnerRevenue}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tenant Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {['starter', 'professional', 'business', 'enterprise'].map((tier) => {
                const tierTenants = tenants.filter(t => t.subscription_tier === tier).length;
                const percentage = tenants.length > 0 ? (tierTenants / tenants.length * 100).toFixed(1) : 0;
                
                return (
                  <div key={tier} className="flex justify-between items-center">
                    <span className="text-sm capitalize">{tier}</span>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm">{tierTenants}</span>
                      <Badge variant="outline">{percentage}%</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};